import React from "react";
import { useTheme } from "@mui/material/styles";
import { TextFieldProps as MuiTextFieldProps, SxProps } from "@mui/material";
import { InfoMessage } from "..";
import { Variants, SizeStyles } from "../TextField/TextField.variants";
import {
  CustomInputLabel,
  CustomTextField,
  TextFieldContainer,
} from "../TextField/TextField.styles";

export interface NumericTextFieldProps
  extends Pick<
    MuiTextFieldProps,
    | "onBlur"
    | "inputRef"
    | "InputProps"
    | "inputProps"
    | "onKeyDown"
    | "fullWidth"
    | "required"
    | "onBlurCapture"
    | "autoFocus"
    | "autoComplete"
    | "id"
  > {
  value?: number | null;
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  label?: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  errorBackground?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactElement | null;
  rightIcon?: React.ReactElement | null;
  disabledIcon?: React.ReactElement | null;
  customInputLabelSx?: SxProps;
  errorMessageSx?: SxProps;
  containerSx?: SxProps;
  className?: string;
  sx?: SxProps;
  hideSpinners?: boolean;
  positiveOnly?: boolean;
  onChange?: (
    value: number | undefined,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const normalizeValue = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "";
  }

  return value;
};

export const NumericTextField = ({
    variant = "primary",
    size = "md",
    label,
    placeholder,
    error = false,
    errorMessage = "",
    errorBackground = false,
    disabled = false,
    disabledIcon = null,
    leftIcon = null,
    rightIcon = null,
    fullWidth,
    autoComplete = "off",
    sx,
    customInputLabelSx,
    containerSx,
    errorMessageSx,
    value,
    className,
    hideSpinners = false,
    positiveOnly = false,
    onChange,
    ...rest
}: NumericTextFieldProps) => {
  const theme = useTheme();

  const variantStylesFn = Variants(theme);
  const sizeStylesFn = SizeStyles(theme);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (positiveOnly && ['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
    rest.onKeyDown?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const parsedValue = value === "" ? undefined : Number(value);
    const numericValue = Number.isNaN(parsedValue) ? undefined : parsedValue;

    onChange?.(numericValue, event);
  };

  return (
    <TextFieldContainer
      sx={fullWidth ? { width: "100%", ...containerSx } : {...containerSx}}
      className={className}
    >
      {label && (
        <CustomInputLabel shrink htmlFor="numeric-textfield-input" sx={customInputLabelSx}>
          {label}
        </CustomInputLabel>
      )}
      <CustomTextField
        type="number"
        id="numeric-textfield-input"
        disabled={disabled}
        placeholder={placeholder}
        InputProps={{
          startAdornment: leftIcon,
          endAdornment: disabled && disabledIcon ? disabledIcon : rightIcon,
        }}
        sx={{
          ...variantStylesFn[variant],
          ...sizeStylesFn[size],
          ...(hideSpinners && {
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
          }),
          ...sx,
        }}
        {...rest}
        value={normalizeValue(value)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputProps={{
          ...rest.inputProps,
          autoComplete
        }}
        className={error ? "is-error" : ""}
      />
      {error && errorMessage !== "" && (
        <InfoMessage
          hasBackground={errorBackground}
          hasIcon={false}
          variant="error"
          message={errorMessage}
          sx={errorMessageSx}
        />
      )}
    </TextFieldContainer>
  );
};

NumericTextField.displayName = "NumericTextField";
